import {
  EAlternativeOption,
  OrderStatusType,
  ReadyStatusAgreements
} from '@/@types/order';
import { Order } from '@/apis/order/interface';
import { GSelectOption } from '@/assets/data/selectOptions';
import { useMemo, useState } from 'react';
import useModal from '../common/useModal';
import usePrescriptionDeny from './usePrescriptionDeny';
import useOrderStatus from '../common/useOrderStatus';
import useSetOrderPrice from './useSetOrderPrice';

const ALTERNATIVE_OPTIONS = [
  {
    label: `처방받은 약품의 재고가 없어서 동일한 성분의 약으로${'\n'}대체 조제 되었어요.`,
    value: EAlternativeOption.SoldOut
  },
  {
    label: `처방받은 약품과 동일한 성분이지만, 더 저렴한 약으로${'\n'}대체 조제 되었어요.`,
    value: EAlternativeOption.Cheaps
  },
  { label: '직접 입력', value: EAlternativeOption.MANUAL }
];

interface Props {
  order: Order;
}

const useOrderReadyViewModel = ({ order }: Props) => {
  const {
    orderDenyReasons,
    cancelReason,
    setCancelReason,
    onSubmitOrderCancelByPharmacy,
    onSubmitPrescriptionDeny,
    PrescriptionDenyModal,
    onChangePrescriptionDenyModal
  } = usePrescriptionDeny(order.orderId);
  const { orderViewStatus, onChangeOrderViewStatus } = useOrderStatus({
    order
  });

  const { setOrderPriceMutate } = useSetOrderPrice();
  const [PriceModal, setPriceModal] = useModal(false);
  const [OrderStatusResetModal, setOrderStatusResetModal] = useModal(false);

  const [agreements, setAgreements] = useState<ReadyStatusAgreements>({
    isCheckedDur: false,
    isCheckedAlternative: undefined
  });
  const [price, setPrice] = useState<number | string>('');
  const [alternativeReason, setAlternativeReason] =
    useState<GSelectOption<EAlternativeOption>>();
  const [alternativeLog, setAlternativeLog] = useState<string>();

  const isRequestPaymentDisabled = useMemo(() => {
    if (price === '') return true;
    if (agreements.isCheckedAlternative && !alternativeReason) {
      return true;
    }
    if (
      alternativeReason?.value === EAlternativeOption.MANUAL &&
      alternativeReason.label === ''
    ) {
      return true;
    }
    return false;
  }, [price, alternativeReason, agreements.isCheckedAlternative]);

  const isPriceInputDisabled = useMemo(() => {
    if (agreements.isCheckedAlternative) {
      if (agreements.isCheckedAlternative === undefined) {
        return true;
      }
      if (!alternativeReason) return true;
      if (
        alternativeReason?.value === EAlternativeOption.MANUAL &&
        alternativeReason.label === ''
      ) {
        return true;
      }
    }

    if (agreements.isCheckedAlternative === undefined) {
      return true;
    }
    return false;
  }, [alternativeReason, agreements.isCheckedAlternative]);

  const onPressAgreement = (
    policy: keyof ReadyStatusAgreements,
    status?: boolean
  ) => {
    setAgreements({
      ...agreements,
      [policy]: status !== undefined ? status : !agreements[policy]
    });
  };

  const onResetOrderStatusInReady = () => {
    setAgreements({
      ...agreements,
      isCheckedDur: false,
      isCheckedAlternative: undefined
    });
    setAlternativeReason({ label: '', value: EAlternativeOption.MANUAL });
    setPrice('');
    setOrderStatusResetModal(false);
    onChangeOrderViewStatus(OrderStatusType.Ready);
  };

  const onChangeAlterNativeLog = (value: string) => {
    return setAlternativeLog(value);
  };

  const onSubmitSetPrice = () => {
    setPriceModal(false);
    setOrderPriceMutate({
      request: {
        orderId: String(order.id),
        price: Number(price),
        useAlternative: agreements.isCheckedAlternative ?? false,
        alternativeReason: alternativeReason?.label,
        alternativeLog
      }
    });
  };

  const onChangeAlternativeReason = (
    option: GSelectOption<EAlternativeOption>,
    reason?: string
  ) => {
    if (option.value === EAlternativeOption.MANUAL) {
      return setAlternativeReason({
        label: reason ?? '',
        value: option.value
      });
    }
    return setAlternativeReason(option);
  };

  return {
    order,
    orderViewStatus,
    onChangeOrderViewStatus,
    agreements,
    onPressAgreement,
    price,
    setPrice,
    orderDenyReasons,
    cancelReason,
    setCancelReason,
    onSubmitOrderCancelByPharmacy,
    onClickResetOrder: () => setOrderStatusResetModal(true),
    alternativeReason,
    onChangeAlternativeReason,
    OrderStatusResetModal,
    onResetOrderStatusInReady,
    setOrderStatusResetModal,
    PriceModal,
    onSubmitSetPrice,
    setPriceModal,
    alternativeOptions: ALTERNATIVE_OPTIONS,
    isRequestPaymentDisabled,
    isPriceInputDisabled,
    PrescriptionDenyModal,
    onChangePrescriptionDenyModal,
    onSubmitPrescriptionDeny,
    alternativeLog,
    onChangeAlterNativeLog
  };
};

export default useOrderReadyViewModel;
